
import {
  Command,
  CommandDialog,
  CommandInput,
} from "@/components/ui/command";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Filter, Keyboard, Search, SortAsc } from "lucide-react";
import { FilterMenu } from "./search/FilterMenu";
import { SortMenu, SortOption } from "./search/SortMenu";
import { SearchResults } from "./search/SearchResults";
import { FilterPills } from "./search/FilterPills";
import { 
  getFilteredProducts, 
  getPrescriptionCount, 
  getOtcCount, 
  recentSearches 
} from "./search/searchUtils";

interface SearchCommandProps {
  selectedCategory?: string | null;
  setSelectedCategory?: (category: string | null) => void;
  sortBy?: SortOption;
  setSortBy?: (option: SortOption) => void;
  filterMenuOpen?: boolean;
  setFilterMenuOpen?: (open: boolean) => void;
  sortMenuOpen?: boolean;
  setSortMenuOpen?: (open: boolean) => void;
  showInHeader?: boolean;
}

export function SearchCommand({
  selectedCategory = null,
  setSelectedCategory = () => {},
  sortBy = "name-asc",
  setSortBy = () => {},
  filterMenuOpen = false,
  setFilterMenuOpen = () => {},
  sortMenuOpen = false,
  setSortMenuOpen = () => {},
  showInHeader = false
}: SearchCommandProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [prescriptionFilter, setPrescriptionFilter] = useState<boolean | null>(null);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const filteredProducts = getFilteredProducts(
    value,
    selectedCategory,
    prescriptionFilter,
    sortBy
  );

  const prescriptionCount = getPrescriptionCount();
  const otcCount = getOtcCount();

  // Handle keyboard navigation in search results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const buttonClasses = showInHeader 
    ? "relative h-9 w-full justify-start rounded-md bg-background text-sm font-normal text-muted-foreground shadow-none sm:pr-12 pl-9"
    : "relative h-10 w-full justify-start rounded-full bg-background text-sm font-normal text-muted-foreground shadow-none sm:pr-12 pl-10";

  const iconClasses = showInHeader
    ? "absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
    : "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground";

  return (
    <>
      <div className="flex flex-col md:w-[500px] w-full">
        <div className="relative flex flex-1 items-center w-full border rounded-full bg-background px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground mr-2" />
          <Button
            variant="ghost"
            className="flex-1 justify-start h-auto p-0 text-sm font-normal text-muted-foreground shadow-none bg-transparent"
            onClick={() => setOpen(true)}
          >
            <span className="hidden lg:inline-flex">Search medications...</span>
            <span className="inline-flex lg:hidden">Search...</span>
            <kbd className="pointer-events-none absolute right-12 top-1/2 hidden h-7 -translate-y-1/2 select-none items-center gap-1 rounded-full border bg-background px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              <span className="text-xs">⌘</span>F
            </kbd>
          </Button>
          
          <div className="flex items-center">
            <FilterPills 
              selectedCategory={selectedCategory} 
              setSelectedCategory={setSelectedCategory} 
            />
          
            <div className="flex items-center ml-auto">
              <FilterMenu
                open={filterMenuOpen}
                setOpen={setFilterMenuOpen}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
              
              <SortMenu
                open={sortMenuOpen}
                setOpen={setSortMenuOpen}
                sortBy={sortBy}
                setSortBy={setSortBy}
              />

              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 shrink-0 ml-1" 
                onClick={() => setOpen(true)}
                aria-label="Open search with keyboard shortcuts"
              >
                <Keyboard className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command className="rounded-xl border shadow-md" onKeyDown={handleKeyDown}>
          <CommandInput 
            placeholder="Type to search..." 
            value={value}
            onValueChange={setValue}
            aria-label="Search medications"
          />
          <SearchResults
            value={value}
            filteredProducts={filteredProducts}
            recentSearches={recentSearches}
            prescriptionCount={prescriptionCount}
            otcCount={otcCount}
            setPrescriptionFilter={setPrescriptionFilter}
            setOpen={setOpen}
          />
        </Command>
      </CommandDialog>
    </>
  );
}
